const SAFE_QUERY_PARAMS = new Set([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
  "dclid",
  "msclkid",
  "fbclid",
  "ttclid",
  "sold_out",
]);

const SENSITIVE_KEYS = new Set([
  "authorization",
  "auth_token",
  "client_secret",
  "customer_email",
  "email",
  "firebase_token",
  "id_token",
  "phone",
  "phone_number",
  "stripe_customer_id",
  "stripe_payment_intent_id",
  "token",
  "user_email",
]);

const EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/g;
const PHONE_RE = /(?:\+?\d[\d\s().-]{7,}\d)/g;

type Jsonish = string | number | boolean | null | Record<string, unknown> | unknown[];

const POSTHOG_SDK_OBJECT_ARRAY_KEYS = new Set([
  "$elements",
  "$exception_list",
  "$snapshot_data",
]);

function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/^\$+/, "");
  if (normalized === "email_present") return false;
  if (SENSITIVE_KEYS.has(normalized)) return true;
  return (
    normalized.includes("authorization") ||
    normalized.includes("client_secret") ||
    normalized.includes("firebase_token") ||
    normalized.includes("id_token") ||
    normalized.endsWith("_token") ||
    normalized.includes("secret")
  );
}

function sanitizeUrl(value: string): string {
  const base = "https://machuping.local";
  let parsed: URL;
  try {
    parsed = value.startsWith("/") ? new URL(value, base) : new URL(value);
  } catch {
    return value.replace(EMAIL_RE, "[redacted_email]").replace(PHONE_RE, "[redacted_phone]").slice(0, 1000);
  }

  const safe = new URL(parsed.pathname, parsed.origin === base ? base : parsed.origin);
  for (const [key, paramValue] of parsed.searchParams.entries()) {
    if (SAFE_QUERY_PARAMS.has(key)) safe.searchParams.set(key, paramValue.slice(0, 500));
  }
  const out = `${safe.pathname}${safe.search}${parsed.hash && parsed.hash.length < 80 ? parsed.hash : ""}`;
  return parsed.origin === base ? out : `${safe.origin}${out}`;
}

function sanitizeString(key: string, value: string): string {
  const trimmed = value.slice(0, 2000);
  if (key === "$raw_user_agent") return trimmed.replace(EMAIL_RE, "[redacted_email]");
  if (
    key === "path" ||
    key === "referrer" ||
    key === "$referrer" ||
    key === "$current_url" ||
    key === "first_landing_path" ||
    key.endsWith("_path") ||
    key.endsWith("_url") ||
    key.endsWith("_href")
  ) {
    return sanitizeUrl(trimmed);
  }
  if (key === "distinct_id" || key.endsWith("_id") || key.endsWith("_key") || key === "$group_key") {
    return trimmed.replace(EMAIL_RE, "[redacted_email]");
  }
  return trimmed.replace(EMAIL_RE, "[redacted_email]").replace(PHONE_RE, "[redacted_phone]");
}

export function sanitizePostHogProperties<T extends Record<string, unknown>>(properties: T): Record<string, Jsonish> {
  const out: Record<string, Jsonish> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined || isSensitiveKey(key)) continue;
    if (value === null || typeof value === "number" || typeof value === "boolean") {
      out[key] = value;
    } else if (typeof value === "string") {
      out[key] = sanitizeString(key, value);
    } else if (Array.isArray(value)) {
      out[key] = value
        .filter((item): item is string | number | boolean | null =>
          item === null || ["string", "number", "boolean"].includes(typeof item),
        )
        .map((item) => (typeof item === "string" ? item.slice(0, 500) : item));
    } else if (key === "$groups") {
      out[key] = value as Record<string, unknown>;
    }
  }
  return out;
}

function preservePostHogSdkProperties(
  event: string | undefined,
  properties: Record<string, unknown>,
  sanitized: Record<string, Jsonish>,
): Record<string, Jsonish> {
  const token = properties.token;
  if (typeof token === "string" && token.startsWith("phc_")) {
    sanitized.token = token;
  }

  for (const key of POSTHOG_SDK_OBJECT_ARRAY_KEYS) {
    const value = properties[key];
    if (Array.isArray(value)) sanitized[key] = value;
  }

  const snapshotBytes = properties.$snapshot_bytes;
  if (event === "$snapshot" && typeof snapshotBytes === "number") {
    sanitized.$snapshot_bytes = snapshotBytes;
  }

  return sanitized;
}

// Markers for $exception events that come from browser extensions, cross-origin
// third-party scripts, or the local dev server — none of which are real product
// bugs and all of which pollute error tracking.
const EXCEPTION_NOISE_RE =
  /chrome-extension:\/\/|moz-extension:\/\/|safari-web-extension:\/\/|safari-extension:\/\/|webkit-masked-url|\bxbrowser\b|contentScriptData|^Script error\.?$|^CustomEvent captured as exception with keys: isTrusted$|ResizeObserver loop|Module build failed|ModuleBuildError|webpack-internal|\/Users\/|\/_next\/static\/.*\.map/i;

function collectExceptionStrings(properties: Record<string, unknown>): string {
  const parts: string[] = [];
  const push = (v: unknown) => {
    if (typeof v === "string") parts.push(v);
    else if (Array.isArray(v)) v.forEach(push);
  };
  push(properties.$exception_values);
  push(properties.$exception_types);
  push(properties.$exception_message);
  const list = properties.$exception_list;
  if (Array.isArray(list)) {
    for (const item of list) {
      if (!item || typeof item !== "object") continue;
      const ex = item as { value?: unknown; type?: unknown; stacktrace?: { frames?: unknown } };
      push(ex.value);
      push(ex.type);
      const frames = ex.stacktrace?.frames;
      if (Array.isArray(frames)) {
        for (const frame of frames) {
          if (frame && typeof frame === "object") push((frame as { filename?: unknown }).filename);
        }
      }
    }
  }
  return parts.join("\n");
}

// Returns true when an $exception capture is browser-extension or dev-server
// noise that should be dropped before it reaches PostHog.
export function isNoiseExceptionCapture(capture: { event?: string; properties: Record<string, unknown> }): boolean {
  if (capture.event !== "$exception") return false;
  return EXCEPTION_NOISE_RE.test(collectExceptionStrings(capture.properties));
}

export function sanitizePostHogCaptureResult<T extends { event?: string; properties: Record<string, unknown>; $set?: Record<string, unknown>; $set_once?: Record<string, unknown> }>(
  capture: T | null,
): T | null {
  if (!capture) return null;
  if (isNoiseExceptionCapture(capture)) return null;
  return {
    ...capture,
    properties: preservePostHogSdkProperties(
      capture.event,
      capture.properties,
      sanitizePostHogProperties(capture.properties),
    ),
    ...(capture.$set ? { $set: sanitizePostHogProperties(capture.$set) } : {}),
    ...(capture.$set_once ? { $set_once: sanitizePostHogProperties(capture.$set_once) } : {}),
  };
}
